/** Pure transforms from the flat DepGraph into the row + adjacency models the
 *  table and gantt strip render. No React state, so they unit-test directly.
 *
 *  The view is "every activity in this section". A Takt code/zone is just how a
 *  bar is styled and grouped — not a filter: every in-scope, still-plannable
 *  activity gets exactly one row. Zoned work groups under its Takt zone; the
 *  rest of the section collects in a trailing "no zone yet" group. Out-of-
 *  section externals and finished work that still gates the section are DRIVERS:
 *  demoted to a collapsed "Drivers & handoffs" group and shown on the canvas as
 *  a compact handoff marker (not a full bar), with a chevron on any section bar
 *  whose driver isn't currently drawn.
 */
import type { DepGraph, DepGraphActivity, DepGraphEdge } from "../../../models";
import { ActivityStatus } from "../../../utils/p6";
import { naturalCompare } from "../../../utils/sort";

/** Header for the in-section, not-yet-zoned group (coded-but-unplaced + uncoded
 *  work). These are first-class section members, not an "untagged" exile. */
export const NO_ZONE_LABEL = "In this section · no zone yet";

/** Fixed table row height — locked to DepTable.css and the strip math so rows
 *  on both panes line up. Any change here must update DepTable.css. */
export const ROW_H = 28;

/** Height of ONE driver lane on an activity row — each pred/succ driver gets
 *  its own lane (so multiples stack, never overlap), the gantt drawing a mini-
 *  bar in it. Must match DepGanttStrip's lane pitch (`DRIVER_H + 4`). */
export const DRIVER_STRIP_H = 18;

/** Extra height an activity row reserves for its driver lanes: one per pred
 *  (top strip) + one per succ (bottom strip). */
export function driverStripsHeight(predCount: number, succCount: number): number {
  return (predCount + succCount) * DRIVER_STRIP_H;
}

/** A predecessor edge resolved against the graph for one row's PRED cell. */
export interface ResolvedPred {
  edge: DepGraphEdge;
  pred: DepGraphActivity;
}

/** A successor edge (this node is the predecessor) resolved for the SUCC cell.
 *  Carries the edge keyed by ITS successor so a retype/remove resolves. */
export interface ResolvedSucc {
  succId: string;
  edge: DepGraphEdge;
  succ: DepGraphActivity;
}

/** A driver (out-of-section external / finished work) attached to ONE activity
 *  row: it gets no row of its own — the gantt draws it as a thin mini-bar in the
 *  row's top strip (a `pred` that gates the activity) or bottom strip (a `succ`
 *  it hands off to), at the driver's own date, with the short dep arrow. */
export interface RowDriver {
  driver: DepGraphActivity;
  edge: DepGraphEdge;
  role: "pred" | "succ";
}

export type RowLayoutEntry =
  | {
      kind: "header";
      key: string;
      label: string;
      count: number;
      /** The group's zone tuple (null for the "no zone yet" group / date mode). */
      zone: { a: string; b: string; c: string } | null;
      /** Muted suffix after the count (anchors view: what a tie added under
       *  this group is — "incoming successors" / "outgoing preds"). */
      hint?: string;
      rowIndex: number;
    }
  | {
      kind: "activity";
      key: string;
      activity: DepGraphActivity;
      /** Placement zone for this row (takt view); null in by-date view. Arrows
       *  pair a duplicated neighbour copy with the bar in the SAME zone. */
      zoneKey: string | null;
      /** Anchors view: this row is an in-section CONTEXT copy laid directly
       *  adjacent to the external (anchor) it's tied to — one copy per pairing,
       *  so the dependency arrow is one short hop. Identifies that external. */
      partnerOf?: string;
      /** "fed" = the external feeds this activity (copy sits beneath it);
       *  "feeder" = this activity feeds the external (copy sits above it). */
      partnerRole?: "feeder" | "fed";
      /** Drivers (externals / finished work) attached to THIS row — no rows of
       *  their own. `predDrivers` gate it (drawn in the row's top strip);
       *  `succDrivers` are fed by it (bottom strip). Empty when drivers hidden. */
      predDrivers: RowDriver[];
      succDrivers: RowDriver[];
      rowIndex: number;
    }
  | {
      /** The inline "+ add activity" row pinned at the bottom of a zone group
       *  (pull-plan editing only — emitted when `withAddRows`). */
      kind: "ghost";
      key: string;
      zone: { a: string; b: string; c: string } | null;
      /** The activity ABOVE the row — the suggested predecessor. */
      lastActivityId: string | null;
      /** The activity BELOW the row (mid-table inserts) — the suggested
       *  successor. Null at the bottom of a level. */
      nextActivityId: string | null;
      rowIndex: number;
    };

/** Map a raw P6 task_type to the row kind. Tasks draw bars; milestones render
 *  as a diamond glyph; everything else as a generic non-task row. */
export function kindFromTaskType(taskType: string): "task" | "milestone" | "other" {
  const t = (taskType || "").toUpperCase();
  if (t === "TT_TASK") return "task";
  if (t === "TT_FINMILE" || t === "TT_STARTMILE" || t === "TT_MILE") return "milestone";
  return "other";
}

/** Ascending ISO workday column axis the gantt strip renders from, spanning the
 *  given activities' dates (±~2 weeks padding). Weekends are dropped; the strip
 *  snaps any bar on an excluded date to a column, so this only needs to be a
 *  dense weekday axis — not holiday-exact. */
export function calendarFromActivities(activities: DepGraphActivity[]): string[] {
  let min: string | null = null;
  let max: string | null = null;
  for (const a of activities) {
    for (const d of [a.start, a.finish, a.actual_start, a.actual_finish, a.drive_start, a.drive_finish]) {
      if (!d) continue;
      if (min === null || d < min) min = d;
      if (max === null || d > max) max = d;
    }
  }
  if (min === null || max === null) return [];

  // Generous padding so the inline driver pills hugging a bar's left/right edge
  // (preds left, hand-offs right) have gutter room and don't clip off the strip.
  const start = new Date(min + "T00:00:00");
  start.setDate(start.getDate() - 12);
  const end = new Date(max + "T00:00:00");
  end.setDate(end.getDate() + 12);

  const out: string[] = [];
  for (const cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
    const dow = cur.getDay();
    if (dow === 0 || dow === 6) continue; // skip weekends
    out.push(cur.toISOString().slice(0, 10));
  }
  return out;
}

/** Axis for the standard plan views, scoped to the CANVAS activities so a
 *  far-dated driver never stretches the timeline: a driver's handoff marker
 *  lands at its drive point (≈ a section bar's start, in-window by
 *  construction), so the axis stays tight whether or not drivers are shown. */
export function calendarFromGraph(graph: DepGraph): string[] {
  return calendarFromActivities(
    Object.values(graph.activities).filter(isCanvasActivity),
  );
}

export type NodeStatus = "active" | "complete" | "not_started";

/** Progress state from the raw P6 status. Drives the active in-progress badge
 *  and the completed "done" treatment; everything else is not-started. */
export function nodeStatus(a: DepGraphActivity): NodeStatus {
  const s = (a.status || "").toUpperCase();
  if (a.complete || s === ActivityStatus.Complete.toUpperCase()) return "complete";
  if (s === ActivityStatus.Active.toUpperCase()) return "active";
  return "not_started";
}

/** How a DRIVER reads in both panes: finished work vs an out-of-section
 *  external, plus the human origin string ("finished work" / its section). The
 *  marker GLYPH stays site-specific (strip `◦` vs table `↗`); only the
 *  classification + origin label are shared so they can't drift apart. */
export function driverDescriptor(a: DepGraphActivity): { done: boolean; origin: string } {
  const done = nodeStatus(a) === "complete";
  return { done, origin: done ? "finished work" : a.section || "another section" };
}

/** The bar/row LABEL. A tagged in-scope activity reads by its work-code name
 *  (Takt_Prefix) — exactly what the board shows — so the same bar reads the same
 *  in both places. Untagged in-section work and cross-section externals keep
 *  their raw P6 task_name (their only meaningful identity). The full task_name
 *  is always available on hover. */
export function activityLabel(a: DepGraphActivity): string {
  return a.in_scope && a.work_code && a.work_name ? a.work_name : a.name;
}

/** "L3" / "BLKA.1-L3" — zone label, dropping empties. */
export function zoneFull(a: DepGraphActivity): string {
  return [a.zone.a, a.zone.b, a.zone.c].filter(Boolean).join("-");
}

export function zoneShort(a: DepGraphActivity): string {
  return a.zone.c || zoneFull(a);
}

function zoneKey(a: DepGraphActivity): string {
  return `${a.zone.a}||${a.zone.b}||${a.zone.c}`;
}

function hasZone(a: DepGraphActivity): boolean {
  return Boolean(a.zone.a || a.zone.b || a.zone.c);
}

/** A CANVAS activity — the section's own, still-plannable work. Every one gets a
 *  row/bar (zoned or not); these are the only editable subjects. */
export function isCanvasActivity(a: DepGraphActivity): boolean {
  return a.in_scope && nodeStatus(a) !== "complete";
}

/** A DRIVER — an out-of-section external or a completed activity that's surfaced
 *  only because it gates the section. Demoted off the canvas into the collapsed
 *  "Drivers & handoffs" group (and shown as a chip on the rows it drives). */
export function isDriver(a: DepGraphActivity): boolean {
  return !isCanvasActivity(a);
}

/** Review key for one outside tie (anchor): "predId>succId". */
export function anchorKeyOf(predId: string, succId: string): string {
  return `${predId}>${succId}`;
}

/** Predecessor edges for one successor id (empty list when none). */
export function predsFor(graph: DepGraph, id: string): ResolvedPred[] {
  const out: ResolvedPred[] = [];
  for (const e of graph.edges[id] ?? []) {
    const pred = graph.activities[e.pred_id];
    if (pred) out.push({ edge: e, pred });
  }
  return out;
}

/** Invert `edges` once into successor adjacency: predId -> its successor edges.
 *  Build this in the caller's memo and reuse for every row's SUCC cell. */
export function buildSuccAdjacency(
  graph: DepGraph,
): Map<string, ResolvedSucc[]> {
  const m = new Map<string, ResolvedSucc[]>();
  for (const [succId, edges] of Object.entries(graph.edges)) {
    const succ = graph.activities[succId];
    if (!succ) continue;
    for (const e of edges) {
      const list = m.get(e.pred_id);
      const entry: ResolvedSucc = { succId, edge: e, succ };
      if (list) list.push(entry);
      else m.set(e.pred_id, [entry]);
    }
  }
  return m;
}

/** The direct predecessor + successor ids of one activity. Drives the
 *  relationship tint: when a row is selected, its preds light up one color and
 *  its succs another, on both the table and the gantt. Built from the same
 *  `edges` map the chips resolve from, so the highlight can't drift from them. */
export interface NeighborRoles {
  preds: Set<string>;
  succs: Set<string>;
}

/** Empty result — a stable reference for "nothing selected" so memoized
 *  consumers don't re-render on every graph change. */
export const NO_NEIGHBORS: NeighborRoles = { preds: new Set(), succs: new Set() };

export function neighborRoles(graph: DepGraph, id: string | null): NeighborRoles {
  if (!id) return NO_NEIGHBORS;
  const preds = new Set<string>();
  const succs = new Set<string>();
  // edges[id] holds the edges whose successor is `id` — i.e. its predecessors.
  for (const e of graph.edges[id] ?? []) preds.add(e.pred_id);
  // Any edge anywhere whose pred is `id` makes its successor a follower.
  for (const [succId, edges] of Object.entries(graph.edges)) {
    for (const e of edges) {
      if (e.pred_id === id) {
        succs.add(succId);
        break;
      }
    }
  }
  return { preds, succs };
}

/** Work code -> the set of zone keys ("a|b|c") it appears in. A chip shows its
 *  zone disambiguator only when its code spans more than one zone. Built once per
 *  graph and shared by the table and the Relationships panel so the rule can't
 *  drift between them. */
export function buildZonesByCode(
  activities: DepGraphActivity[],
): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  for (const a of activities) {
    const key = `${a.zone.a}|${a.zone.b}|${a.zone.c}`;
    let set = m.get(a.work_code);
    if (!set) {
      set = new Set();
      m.set(a.work_code, set);
    }
    set.add(key);
  }
  return m;
}

/** The date a row plots at: the RETAINED (remaining-early / drive) start or
 *  finish, falling back to the display column when there's no retained point.
 *  The single source for the `drive_start ?? start` selection used by the dep
 *  rows, byStartDate, and driverWindow — change the rule here, not at each site. */
export function rowStart(a: DepGraphActivity): string | null {
  return a.drive_start ?? a.start;
}
export function rowFinish(a: DepGraphActivity): string | null {
  return a.drive_finish ?? a.finish;
}

/** Earliest start first (finish fallback); undated rows sink to the bottom.
 *  Sorts on the RETAINED (drive) start, so row order is identical in Board and
 *  Forward-pass mode — an in-progress activity orders by its remaining start,
 *  not its actual start (which would reshuffle rows the moment the pass runs). */
export function byStartDate(a: DepGraphActivity, b: DepGraphActivity): number {
  const key = (x: DepGraphActivity) => rowStart(x) || rowFinish(x) || "";
  const ka = key(a);
  const kb = key(b);
  if ((ka === "") !== (kb === "")) return ka === "" ? 1 : -1;
  if (ka !== kb) return ka < kb ? -1 : 1;
  return a.work_code.localeCompare(b.work_code);
}

/** How many drivers (externals + finished work) the graph has — drives the
 *  header "External Ties" toggle count. */
export function countDrivers(graph: DepGraph): number {
  return Object.values(graph.activities).filter(isDriver).length;
}

/** Where a driver's read-only bar sits on the strip — its DRIVING window. A
 *  COMPLETED driver sits at its RETAINED EARLY FINISH = max(data date, the
 *  retained CPM point it drives from): the data date for an in-sequence
 *  completed (done as of now), but LATER for one held out of sequence by its own
 *  predecessors (which drives successors from that held date, not the data date).
 *  Everything else uses its remaining-early (drive) window. */
export function driverWindow(
  d: DepGraphActivity,
  dataDate: string | null,
): { start: string | null; finish: string | null } {
  if (nodeStatus(d) === "complete") {
    const point = d.drive_finish ?? d.finish;
    const slot =
      dataDate && point ? (point > dataDate ? point : dataDate) : point ?? dataDate;
    return { start: slot, finish: slot };
  }
  return { start: rowStart(d), finish: rowFinish(d) };
}

/** Which retained date a driver mini-bar shows (and connects its arrow at): the
 *  end of THIS external the tie keys off — a predecessor uses its edge's FIRST
 *  letter (FS/FF → finish, SS/SF → start), a successor its SECOND (FS/SS → start,
 *  FF/SF → finish). Reads the already-retained `driverWindow`, so a completed
 *  driver returns its single retained point and the date always matches where the
 *  mini-bar sits — never the actual finish. */
export function driverDate(
  role: "pred" | "succ",
  edgeType: DepGraphEdge["type"],
  win: { start: string | null; finish: string | null },
): string | null {
  const end = role === "pred" ? edgeType[0] : edgeType[1];
  return end === "F" ? win.finish : win.start;
}

/**
 * Build the flat row sequence both panes iterate. EVERY in-scope, still-
 * plannable activity gets exactly one row — a Takt code/zone only styles and
 * groups it, never gates whether it shows. When `showDrivers`, each DRIVER
 * (external / finished work) is interleaved as a thin read-only row directly
 * ADJACENT to the in-section activity it relates to: predecessors just above it,
 * hand-offs just below — so its dependency arrow is one short hop, not a
 * cross-board line. A driver gating N activities appears once above each.
 *
 *   mode "zone": a header per Takt zone (takt_a asc / takt_b asc / takt_c desc)
 *     with its bars by date, then a trailing "no zone yet" group for the rest of
 *     the section (coded-but-unplaced + uncoded).
 *   mode "date": no grouping — every section bar as a flat row by start date.
 */
export function computeRowLayout(
  graph: DepGraph,
  mode: "zone" | "date",
  showDrivers = true,
  withAddRows = false,
  zoneOrder?: Array<{ a: string; b: string; c: string }>,
  /** PROTOTYPE: group rows by WBS LEAF instead of Takt zone — every canvas
   *  activity lands under its leaf (no "no zone yet" bucket; takt is display
   *  metadata only). Wins over zoneOrder when provided. */
  leafOf?: (id: string) => { key: string; label: string; order: number } | null,
): RowLayoutEntry[] {
  const canvas = Object.values(graph.activities).filter(isCanvasActivity);
  const succAdj = buildSuccAdjacency(graph);

  // Renumber rowIndex to the final array position so both panes index the same row.
  const renumber = (out: RowLayoutEntry[]): RowLayoutEntry[] => {
    out.forEach((e, i) => (e.rowIndex = i));
    return out;
  };

  // ONE row per in-section activity, carrying its drivers (when shown): pred
  // drivers gate it (top strip), succ drivers are fed by it (bottom strip). They
  // get no rows of their own — the gantt draws each as a mini-bar inside this
  // row at the driver's date.
  const emitActivity = (a: DepGraphActivity, zk: string | null): RowLayoutEntry[] => {
    const predDrivers: RowDriver[] = [];
    const succDrivers: RowDriver[] = [];
    if (showDrivers) {
      for (const p of predsFor(graph, a.id)) {
        if (isDriver(p.pred)) predDrivers.push({ driver: p.pred, edge: p.edge, role: "pred" });
      }
      for (const s of succAdj.get(a.id) ?? []) {
        if (isDriver(s.succ)) succDrivers.push({ driver: s.succ, edge: s.edge, role: "succ" });
      }
    }
    return [{ kind: "activity", key: a.id, activity: a, zoneKey: zk, predDrivers, succDrivers, rowIndex: 0 }];
  };

  if (mode === "date") {
    const out: RowLayoutEntry[] = [];
    for (const a of [...canvas].sort(byStartDate)) out.push(...emitActivity(a, null));
    return renumber(out);
  }

  if (leafOf) {
    interface LeafBucket {
      label: string;
      order: number;
      items: DepGraphActivity[];
    }
    const leafBuckets = new Map<string, LeafBucket>();
    for (const a of canvas) {
      const leaf = leafOf(a.id) ?? {
        key: "(no WBS leaf)",
        label: "(no WBS leaf)",
        order: Number.MAX_SAFE_INTEGER,
      };
      let b = leafBuckets.get(leaf.key);
      if (!b) {
        b = { label: leaf.label, order: leaf.order, items: [] };
        leafBuckets.set(leaf.key, b);
      }
      b.items.push(a);
    }
    const out: RowLayoutEntry[] = [];
    const keys = Array.from(leafBuckets.keys()).sort((x, y) => {
      const a = leafBuckets.get(x)!;
      const b = leafBuckets.get(y)!;
      return a.order - b.order || naturalCompare(a.label, b.label);
    });
    for (const key of keys) {
      const b = leafBuckets.get(key)!;
      out.push({ kind: "header", key: `h-${key}`, label: b.label, count: b.items.length, zone: null, rowIndex: 0 });
      const sorted = [...b.items].sort(byStartDate);
      for (const act of sorted) out.push(...emitActivity(act, null));
      if (withAddRows) {
        out.push({
          kind: "ghost",
          key: `g-${key}`,
          zone: null,
          lastActivityId: sorted.length > 0 ? sorted[sorted.length - 1].id : null,
          nextActivityId: null,
          rowIndex: 0,
        });
      }
    }
    return renumber(out);
  }

  interface Bucket {
    label: string;
    items: DepGraphActivity[];
    a: string;
    b: string;
    c: string;
  }
  const buckets = new Map<string, Bucket>();
  for (const a of canvas) {
    if (!hasZone(a)) continue;
    const key = zoneKey(a);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { label: zoneFull(a), items: [], a: a.zone.a, b: a.zone.b, c: a.zone.c };
      buckets.set(key, bucket);
    }
    bucket.items.push(a);
  }

  // Group order follows the BOARD's row order when the host supplies it
  // (zoneOrder = the grid's rows, display order, matched case-insensitively) —
  // the two views must agree on level order, and the board's order is the
  // truth Publish persists into Takt_SortB. Board levels with no activities
  // still get a group at their board position (an empty level is somewhere
  // you ADD work, not a secret). Zones only the graph knows about (and the
  // no-boardZones contexts, e.g. the takt-board dep editor) fall back to a
  // natural sort: takt_a asc / takt_b asc / takt_c desc, number-aware so
  // LVL10 lands above LVL2.
  const fold = (a: string, b: string, c: string) =>
    `${a}||${b}||${c}`.toUpperCase();
  const bucketKeyByFold = new Map<string, string>();
  buckets.forEach((bucket, key) => {
    bucketKeyByFold.set(fold(bucket.a, bucket.b, bucket.c), key);
  });

  const out: RowLayoutEntry[] = [];
  const emitGroup = (
    key: string,
    zone: { a: string; b: string; c: string },
    label: string,
    items: DepGraphActivity[],
  ) => {
    out.push({ kind: "header", key: `h-${key}`, label, count: items.length, zone, rowIndex: 0 });
    const sorted = [...items].sort(byStartDate);
    for (const act of sorted) out.push(...emitActivity(act, key));
    if (withAddRows) {
      out.push({
        kind: "ghost",
        key: `g-${key}`,
        zone,
        lastActivityId: sorted.length > 0 ? sorted[sorted.length - 1].id : null,
        nextActivityId: null,
        rowIndex: 0,
      });
    }
  };

  const emitted = new Set<string>();
  for (const z of zoneOrder ?? []) {
    if (!z.a && !z.b && !z.c) continue;
    const f = fold(z.a, z.b, z.c);
    if (emitted.has(f)) continue;
    emitted.add(f);
    const bucketKey = bucketKeyByFold.get(f);
    if (bucketKey != null) {
      const bucket = buckets.get(bucketKey)!;
      emitGroup(bucketKey, { a: bucket.a, b: bucket.b, c: bucket.c }, bucket.label, bucket.items);
    } else {
      const key = `${z.a}||${z.b}||${z.c}`;
      emitGroup(key, z, [z.a, z.b, z.c].filter(Boolean).join("-"), []);
    }
  }

  const leftovers = Array.from(buckets.keys())
    .filter((key) => {
      const bucket = buckets.get(key)!;
      return !emitted.has(fold(bucket.a, bucket.b, bucket.c));
    })
    .sort((ka, kb) => {
      const a = buckets.get(ka)!;
      const b = buckets.get(kb)!;
      return (
        naturalCompare(a.a, b.a) ||
        naturalCompare(a.b, b.b) ||
        naturalCompare(b.c, a.c) // takt_c desc
      );
    });
  for (const key of leftovers) {
    const bucket = buckets.get(key)!;
    emitGroup(key, { a: bucket.a, b: bucket.b, c: bucket.c }, bucket.label, bucket.items);
  }

  const unzoned = canvas.filter((a) => !hasZone(a)).sort(byStartDate);
  if (unzoned.length > 0) {
    out.push({ kind: "header", key: "h-nozone", label: NO_ZONE_LABEL, count: unzoned.length, zone: null, rowIndex: 0 });
    for (const act of unzoned) out.push(...emitActivity(act, null));
    if (withAddRows) {
      out.push({
        kind: "ghost",
        key: "g-nozone",
        zone: null,
        lastActivityId: unzoned.length > 0 ? unzoned[unzoned.length - 1].id : null,
        nextActivityId: null,
        rowIndex: 0,
      });
    }
  }

  return renumber(out);
}
